import React, { FC, MouseEvent } from 'react';
import { CustomTooltip } from '@components/CustomTooltip';
import { IG_TIERS, ImplementationGroup } from '@constants/entities/project';
import {
  Box,
  Button,
  Chip,
  Paper,
  PaperProps,
  Stack,
  Typography,
} from '@mui/material';
import { themePalette } from '@theme/muiTheme';
import { CheckIcon, InfoCircleEmptyIcon } from '@utils/iconsDefs';
import { formatShowSummary } from '@utils/implementationGroup';
import styled from 'styled-components';

const LegendIcon = styled(InfoCircleEmptyIcon)`
  min-width: 20px;
  height: 20px;

  fill: ${themePalette.grey[700]};

  &:hover {
    fill: ${themePalette.grey[900]};
  }
`;

const legendTitle = (
  <Typography
    component="span"
    variant="h6"
    color="white"
    fontSize={12}
    fontWeight={600}
  >
    What the IG badge means
  </Typography>
);

const legendSubTitle = (
  <Typography component="span" variant="body2" color="white" fontWeight={500}>
    The Implementation Group to which a safeguard applies: <br />
    IG1 → IG1, IG2, IG3 <br />
    IG2 → IG2, IG3 <br />
    IG3 → IG3 <br />
    Top-level controls carry no badge
  </Typography>
);

export type CisDropdownPaperProps = PaperProps & {
  activeTiers?: ImplementationGroup[];
  onTiersChange?: (tiers: ImplementationGroup[]) => void;
  ownerState?: unknown;
};

const CisDropdownPaper: FC<CisDropdownPaperProps> = ({
  children,
  activeTiers = [],
  onTiersChange,
  ownerState,
  ...paperProps
}) => {
  const preventBlur = (event: MouseEvent) => {
    event.preventDefault();
  };

  const toggleTier = (tier: ImplementationGroup) => () => {
    onTiersChange?.(
      activeTiers.includes(tier)
        ? activeTiers.filter((active) => active !== tier)
        : activeTiers.concat(tier),
    );
  };

  return (
    <Paper {...paperProps}>
      <Stack
        direction="row"
        alignItems="center"
        gap={2}
        sx={{ p: '8px 12px' }}
        onMouseDown={preventBlur}
      >
        <Typography variant="body2" color="grey.900">
          Show
        </Typography>

        {IG_TIERS.map((tier) => {
          const isActive = activeTiers.includes(tier);

          return (
            <Chip
              key={tier}
              label={tier}
              size="small"
              variant="grey-outlined"
              className={isActive ? 'selected' : ''}
              icon={
                isActive ? (
                  <CheckIcon fill={themePalette.primary.main} />
                ) : undefined
              }
              onClick={toggleTier(tier)}
              sx={{
                cursor: 'pointer',

                '&.selected': {
                  backgroundColor: themePalette.green[500],
                  borderColor: themePalette.blue[900],
                  color: themePalette.grey[1000],
                },

                '& .MuiChip-icon': {
                  width: '12px',
                  height: '9px',
                  minWidth: '12px',
                  margin: '0 4px 0 0',
                },
              }}
            />
          );
        })}

        <CustomTooltip title={legendTitle} subtitle={legendSubTitle}>
          <LegendIcon />
        </CustomTooltip>

        <Box sx={{ flex: 1 }} />

        {!!activeTiers.length && (
          <Button
            variant="under-text"
            sx={{ '&&': { height: 'auto', p: 0 } }}
            onClick={() => onTiersChange?.([])}
          >
            Reset
          </Button>
        )}
      </Stack>

      {children}

      <Typography
        variant="body2"
        color="grey.800"
        sx={{ p: '8px 12px' }}
        onMouseDown={preventBlur}
      >
        {formatShowSummary(activeTiers)}
      </Typography>
    </Paper>
  );
};

export default CisDropdownPaper;
